import React from 'react'
import DOMPurify from 'dompurify'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'

import api from './api'

const BUILD_A_BEAR = [
    '     .--.     .--.        +-------------+',
    '    (    \\___/    )       |   CLAUSE    |',
    '      \\  o   o  /         | ___________ |',
    '      |    Y    |         ||___________||',
    '       \\  \\_/  /          |_____________|',
    "        '.___.'               /     \\",
    '        /|   |\\              /_______\\',
    '       / |   | \\====[__] *',
    '      (_ |___| _)    ||',
    '        /_/ \\_\\      ||'
].join('\n')

const DEBUG_MODELS = [
    { value: 'haiku', label: 'Haiku 5' },
    { value: 'sonnet', label: 'Sonnet 5' },
    { value: 'opus', label: 'Opus 5' },
    { value: 'gpt-sol', label: 'GPT-5.6 Sol' },
    { value: 'gpt-terra', label: 'GPT-5.6 Terra' },
    { value: 'gpt-luna', label: 'GPT-5.6 Luna' }
]

function DebugPage() {
    const documentInputRef = React.useRef(null)
    const clauseAnalysisInputRef = React.useRef(null)
    const [systemPrompt, setSystemPrompt] = React.useState('')
    const [selectedModel, setSelectedModel] = React.useState('opus')
    const [clauseInstructions, setClauseInstructions] = React.useState('')
    const [contractTypes, setContractTypes] = React.useState([])
    const [selectedContractType, setSelectedContractType] = React.useState('')
    const [selectedClause, setSelectedClause] = React.useState('')
    const [loadedSystemPrompt, setLoadedSystemPrompt] = React.useState('')
    const [promptTab, setPromptTab] = React.useState(0)
    const [clauseAnalysisFileName, setClauseAnalysisFileName] = React.useState('')
    const [documentFile, setDocumentFile] = React.useState(null)
    const [result, setResult] = React.useState(null)
    const [error, setError] = React.useState('')
    const [isLoading, setIsLoading] = React.useState(false)
    const [isConvertingClauseAnalysis, setIsConvertingClauseAnalysis] =
        React.useState(false)

    React.useEffect(() => {
        let active = true
        api.get('/debug/prompts')
            .then(response => {
                if (active) {
                    setContractTypes(
                        (response.data.contractTypes || []).filter(
                            contractType => contractType.name !== 'Form 10-Q'
                        )
                    )
                }
            })
            .catch(() => {
                if (active) {
                    setError('Could not load the existing analysis prompts.')
                }
            })
        return () => {
            active = false
        }
    }, [])

    const handleExistingClauseChange = async event => {
        const clause = event.target.value
        setSelectedClause(clause)
        if (!selectedContractType || !clause) {
            return
        }
        setError('')
        try {
            const response = await api.get('/debug/prompts', {
                params: { contractType: selectedContractType, clauseType: clause }
            })
            setClauseInstructions(response.data.analysisPrompt)
            setLoadedSystemPrompt(response.data.systemPrompt)
            setClauseAnalysisFileName('')
            setResult(null)
        } catch (requestError) {
            setError(
                requestError.response?.data?.error ||
                    'Could not load the selected analysis prompt.'
            )
        }
    }

    const canSubmit =
        clauseInstructions.trim() &&
        documentFile &&
        !isLoading &&
        !isConvertingClauseAnalysis

    const handleFileChange = event => {
        const file = event.target.files?.[0] || null
        setDocumentFile(file)
        setResult(null)
        setError('')
    }

    const handleClauseAnalysisFileChange = async event => {
        const file = event.target.files?.[0] || null
        if (!file) {
            return
        }
        setIsConvertingClauseAnalysis(true)
        setError('')
        try {
            let markdown
            if (file.name.toLowerCase().endsWith('.md')) {
                markdown = await file.text()
            } else if (file.name.toLowerCase().endsWith('.docx')) {
                const data = new FormData()
                data.append('clauseAnalysis', file)
                const response = await api.post(
                    '/debug/convert-clause-analysis',
                    data
                )
                markdown = response.data.markdown
            } else {
                throw new Error('Unsupported clause analysis format')
            }
            setClauseInstructions(markdown)
            setClauseAnalysisFileName(file.name)
            setResult(null)
        } catch (fileError) {
            setError(
                fileError.response?.data?.error ||
                    'Could not convert the clause analysis file to Markdown.'
            )
        } finally {
            setIsConvertingClauseAnalysis(false)
        }
    }

    const handleReset = () => {
        setSystemPrompt('')
        setSelectedModel('opus')
        setClauseInstructions('')
        setSelectedContractType('')
        setSelectedClause('')
        setLoadedSystemPrompt('')
        setPromptTab(0)
        setClauseAnalysisFileName('')
        setDocumentFile(null)
        setResult(null)
        setError('')
        if (documentInputRef.current) {
            documentInputRef.current.value = ''
        }
        if (clauseAnalysisInputRef.current) {
            clauseAnalysisInputRef.current.value = ''
        }
    }

    const handleSubmit = async event => {
        event.preventDefault()
        if (!canSubmit) {
            return
        }

        const data = new FormData()
        data.append('systemPrompt', systemPrompt.trim())
        data.append('model', selectedModel)
        data.append('analysisPrompt', clauseInstructions.trim())
        data.append('document', documentFile)

        setIsLoading(true)
        setResult(null)
        setError('')
        try {
            const response = await api.post('/debug/analyze_clause', data)
            setResult(response.data)
        } catch (requestError) {
            setError(
                requestError.response?.data?.error ||
                    'The analysis request failed. Please try again.'
            )
        } finally {
            setIsLoading(false)
        }
    }

    const sanitizedAnalysis = result?.analysis
        ? DOMPurify.sanitize(result.analysis)
        : ''

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#f3f0e8',
                py: { xs: 2, sm: 5 }
            }}
        >
            <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 } }}>
                <Box
                    component="header"
                    sx={{
                        mb: 3,
                        px: { xs: 3, md: 5 },
                        py: { xs: 3, md: 4 },
                        borderRadius: '18px',
                        color: '#f8fafc',
                        background:
                            'linear-gradient(125deg, #172554 0%, #1e3a5f 62%, #0f766e 100%)',
                        boxShadow: '0 22px 55px rgba(15, 23, 42, 0.2)'
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                            gap: 3,
                            alignItems: 'center'
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#99f6e4',
                                    fontSize: '0.75rem',
                                    fontWeight: 800,
                                    letterSpacing: '0.18em',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Prompt Lab / Debug
                            </Typography>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{ mt: 1, fontWeight: 750 }}
                            >
                                Build-A-Clause
                            </Typography>
                            <Button
                                href="/"
                                variant="text"
                                sx={{
                                    mt: 2,
                                    px: 0,
                                    color: '#99f6e4',
                                    textTransform: 'none'
                                }}
                            >
                                Back to document analysis
                            </Button>
                        </Box>
                        <Box
                            component="pre"
                            role="img"
                            aria-label="Teddy bear"
                            sx={{
                                m: 0,
                                color: '#ccfbf1',
                                fontFamily: 'monospace',
                                fontSize: { xs: '0.62rem', md: '0.9rem' },
                                lineHeight: 1.15,
                                justifySelf: { xs: 'start', sm: 'end' },
                                maxWidth: '100%',
                                overflowX: 'auto'
                            }}
                        >
                            {BUILD_A_BEAR}
                        </Box>
                    </Box>
                </Box>

                {result ? (
                    <Paper
                        elevation={0}
                        sx={{ mb: 3, p: { xs: 2.5, md: 4 }, borderRadius: '16px' }}
                    >
                        <Typography
                            sx={{
                                color: '#0f766e',
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase'
                            }}
                        >
                            Analysis result
                        </Typography>
                        {!result.found ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                The clause was not found in the uploaded document.
                                {result.reason ? ` ${result.reason}` : ''}
                            </Alert>
                        ) : (
                            <Box
                                sx={{
                                    mt: 2,
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'minmax(0, 1fr)',
                                        md: 'minmax(0, 1fr) 320px'
                                    },
                                    gap: 3,
                                    alignItems: 'start'
                                }}
                            >
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                        Identified clause: {result.clauseName}
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            mt: 1.5,
                                            p: 2,
                                            borderLeft: '4px solid #0f766e',
                                            bgcolor: '#f0fdfa',
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'anywhere',
                                            fontFamily: 'inherit',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {result.clauseText}
                                    </Box>
                                </Box>
                                <Box className="comment-panel" sx={{ maxWidth: '100%' }}>
                                    <div className="comment-panel__header">
                                        Analysis comments
                                    </div>
                                    <div
                                        className="comment-panel__item is-expanded"
                                        style={{ cursor: 'default' }}
                                    >
                                        <div className="comment-panel__title">
                                            {result.clauseName}
                                        </div>
                                        <div
                                            className="comment-panel__body debug-analysis-result"
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizedAnalysis
                                            }}
                                        />
                                    </div>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                ) : null}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(300px, 2fr)' },
                        gap: 3,
                        alignItems: 'start'
                    }}
                >
                    <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: '16px' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                    Analysis recipe
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    component="div"
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: 750 }}
                                >
                                    Analysis model
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    value={selectedModel}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setSelectedModel(value)
                                        }
                                    }}
                                    aria-label="Analysis model"
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, minmax(0, 1fr))',
                                            sm: 'repeat(3, minmax(0, 1fr))'
                                        },
                                        gap: 1,
                                        '& .MuiToggleButtonGroup-grouped': {
                                            m: 0,
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '8px !important',
                                            textTransform: 'none'
                                        }
                                    }}
                                >
                                    {DEBUG_MODELS.map(model => (
                                        <ToggleButton
                                            key={model.value}
                                            value={model.value}
                                            aria-label={model.label}
                                        >
                                            {model.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                            <Box>
                                <Tabs
                                    value={promptTab}
                                    onChange={(event, value) => setPromptTab(value)}
                                    aria-label="Prompt sections"
                                    sx={{ mb: 3, borderBottom: '1px solid #e2e8f0' }}
                                >
                                    <Tab label="Clause" id="prompt-tab-0" aria-controls="prompt-panel-0" />
                                    <Tab label="System prompt" id="prompt-tab-1" aria-controls="prompt-panel-1" />
                                </Tabs>
                                <Stack
                                    id="prompt-panel-0"
                                    role="tabpanel"
                                    aria-labelledby="prompt-tab-0"
                                    hidden={promptTab !== 0}
                                    spacing={3}
                                >
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 2
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel id="debug-contract-type-label">
                                                Existing contract type
                                            </InputLabel>
                                            <Select
                                                labelId="debug-contract-type-label"
                                                label="Existing contract type"
                                                value={selectedContractType}
                                                onChange={event => {
                                                    setSelectedContractType(event.target.value)
                                                    setSelectedClause('')
                                                    setLoadedSystemPrompt('')
                                                }}
                                            >
                                                {contractTypes.map(contractType => (
                                                    <MenuItem
                                                        key={contractType.name}
                                                        value={contractType.name}
                                                    >
                                                        {contractType.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth disabled={!selectedContractType}>
                                            <InputLabel id="debug-clause-label">
                                                Existing clause
                                            </InputLabel>
                                            <Select
                                                labelId="debug-clause-label"
                                                label="Existing clause"
                                                value={selectedClause}
                                                onChange={handleExistingClauseChange}
                                            >
                                                {(contractTypes.find(
                                                    item => item.name === selectedContractType
                                                )?.clauses || []).map(clause => (
                                                    <MenuItem key={clause} value={clause}>
                                                        {clause}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box>
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            disabled={isConvertingClauseAnalysis || isLoading}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {clauseAnalysisFileName
                                                ? 'Replace clause analysis'
                                                : 'Upload clause analysis (.md or .docx)'}
                                            <input
                                                ref={clauseAnalysisInputRef}
                                                type="file"
                                                accept=".md,.docx,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={handleClauseAnalysisFileChange}
                                                hidden
                                            />
                                        </Button>
                                        {isConvertingClauseAnalysis ? (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{ mt: 1 }}
                                            >
                                                <CircularProgress size={16} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Converting to Markdown...
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                        {clauseAnalysisFileName ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 1 }}
                                            >
                                                Loaded {clauseAnalysisFileName}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <TextField
                                        label="Analysis prompt Markdown"
                                        value={clauseInstructions}
                                        onChange={event => setClauseInstructions(event.target.value)}
                                        multiline
                                        minRows={9}
                                        required
                                        fullWidth
                                        helperText="Paste a recipe or upload one above. Include clause_name metadata or a Markdown title."
                                    />
                                </Stack>
                                <Stack
                                    id="prompt-panel-1"
                                    role="tabpanel"
                                    aria-labelledby="prompt-tab-1"
                                    hidden={promptTab !== 1}
                                    spacing={2}
                                >
                                    <TextField
                                        label="System prompt (optional)"
                                        value={systemPrompt}
                                        onChange={event => setSystemPrompt(event.target.value)}
                                        multiline
                                        minRows={9}
                                        fullWidth
                                        helperText="Leave blank to use the default legal analysis prompt."
                                    />
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        disabled={!loadedSystemPrompt}
                                        onClick={() => setSystemPrompt(loadedSystemPrompt)}
                                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                                    >
                                        Preload system prompt
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>

                    <Stack spacing={3}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px' }}>
                            <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                Test Privacy Document
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                sx={{ mt: 2, py: 1.4, textTransform: 'none' }}
                            >
                                {documentFile ? 'Replace document' : 'Choose document'}
                                <input
                                    ref={documentInputRef}
                                    type="file"
                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={handleFileChange}
                                    hidden
                                />
                            </Button>
                            {documentFile ? (
                                <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#ecfdf5' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                        {documentFile.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {(documentFile.size / 1024).toFixed(1)} KB
                                    </Typography>
                                </Box>
                            ) : null}
                            <Divider sx={{ my: 2.5 }} />
                            <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.5}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!canSubmit}
                                    size="large"
                                    sx={{ textTransform: 'none' }}
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Find and analyze clause'
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="text"
                                    onClick={handleReset}
                                    disabled={isLoading}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        </Paper>

                        {error ? <Alert severity="error">{error}</Alert> : null}
                    </Stack>
                </Box>

            </Container>
        </Box>
    )
}

export default DebugPage
